import React, { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "../../atoms/Button";
import { FinancialDataGrid } from "../FinancialDataGrid";
import { CustomerProjectionForm } from "../CustomerProjectionForm";
import { SubscriptionTierForm } from "../SubscriptionTierForm";
import { Modal } from "../Modal";
import type {
  Expense,
  Revenue,
  TimelineEvent,
  CustomerProjection,
  SubscriptionTier,
  CustomerCapacityGroup,
  TeamMember,
} from "../../../types/startup.types";
import {
  getRevenuePlanCardSummary,
  getCustomerProjectionCardSummary,
} from "../../../utils/chart/customer-projection.utils";

export interface EditViewGridProps {
  startupId: string;
  expenses: Expense[];
  revenues: Revenue[];
  events: TimelineEvent[];
  customerProjections: CustomerProjection[];
  subscriptionTiers: SubscriptionTier[];
  capacityGroups?: CustomerCapacityGroup[];
  teamMembers?: TeamMember[];
  readOnly?: boolean;
  onExpenseClick?: (expenseId: string) => void;
  onRevenueClick?: (revenueId: string) => void;
  onEventClick?: (eventId: string) => void;
  onCustomerProjectionClick?: (projectionId: string) => void;
  onSubscriptionTierClick?: (tierId: string) => void;
  onCapacityGroupClick?: (groupId: string) => void;
  onAddExpense?: () => void;
  onAddRevenue?: () => void;
  onAddEvent?: () => void;
  onAddCapacityGroup?: () => void;
  onRevisionReviseAgain?: (payload: {
    kind: import("../../../utils/chart/segment-grouping.utils").RevisionItemKind;
    segmentId: string;
    groupId: string;
  }) => void;
  className?: string;
}

type TabType =
  | "overview"
  | "expenses"
  | "revenues"
  | "events"
  | "customers"
  | "subscriptions"
  | "capacity"
  | "team";
type FormType =
  | "expense"
  | "revenue"
  | "event"
  | "customer"
  | "subscription"
  | null;

const EditViewGrid: React.FC<EditViewGridProps> = ({
  startupId,
  expenses,
  revenues,
  events,
  customerProjections,
  subscriptionTiers,
  capacityGroups = [],
  teamMembers = [],
  readOnly = false,
  onExpenseClick,
  onRevenueClick,
  onEventClick,
  onCustomerProjectionClick,
  onSubscriptionTierClick,
  onCapacityGroupClick,
  onAddExpense,
  onAddRevenue,
  onAddEvent,
  onAddCapacityGroup,
  onRevisionReviseAgain,
  className = "",
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showForm, setShowForm] = useState<FormType>(null);

  const tabs = [
    {
      id: "overview",
      label: "All Data",
      count:
        expenses.length +
        revenues.length +
        events.length +
        customerProjections.length +
        subscriptionTiers.length +
        teamMembers.length,
    },
    { id: "expenses", label: "Expenses", count: expenses.length },
    { id: "revenues", label: "Revenues", count: revenues.length },
    { id: "events", label: "Events", count: events.length },
    {
      id: "customers",
      label: "Customer Projections",
      count: customerProjections.length,
    },
    {
      id: "subscriptions",
      label: "Revenue plans",
      count: subscriptionTiers.length,
    },
    {
      id: "capacity",
      label: "Capacity groups",
      count: capacityGroups.length,
    },
    {
      id: "team",
      label: "Team",
      count: teamMembers.length,
    },
  ] as const;

  const getTabData = () => {
    switch (activeTab) {
      case "expenses":
        return {
          expenses,
          revenues: [],
          events: [],
          customerProjections: [],
          subscriptionTiers: [],
        };
      case "revenues":
        return {
          expenses: [],
          revenues,
          events: [],
          customerProjections: [],
          subscriptionTiers: [],
        };
      case "events":
        return {
          expenses: [],
          revenues: [],
          events,
          customerProjections: [],
          subscriptionTiers: [],
        };
      case "customers":
        return {
          expenses: [],
          revenues: [],
          events: [],
          customerProjections,
          subscriptionTiers: [],
        };
      case "subscriptions":
        return {
          expenses: [],
          revenues: [],
          events: [],
          customerProjections: [],
          subscriptionTiers,
        };
      case "capacity":
        return {
          expenses: [],
          revenues: [],
          events: [],
          customerProjections: [],
          subscriptionTiers: [],
        };
      default:
        return {
          expenses,
          revenues,
          events,
          customerProjections,
          subscriptionTiers,
        };
    }
  };

  const tabData = getTabData();

  const handleAddNew = () => {
    switch (activeTab) {
      case "expenses":
        if (onAddExpense) {
          onAddExpense();
        } else {
          setShowForm("expense");
        }
        break;
      case "revenues":
        if (onAddRevenue) {
          onAddRevenue();
        } else {
          setShowForm("revenue");
        }
        break;
      case "events":
        if (onAddEvent) {
          onAddEvent();
        } else {
          setShowForm("event");
        }
        break;
      case "customers":
        setShowForm("customer");
        break;
      case "subscriptions":
        setShowForm("subscription");
        break;
      case "capacity":
        onAddCapacityGroup?.();
        break;
      default:
        // For overview, show a menu or default to expense
        if (onAddExpense) {
          onAddExpense();
        } else {
          setShowForm("expense");
        }
    }
  };

  const getAddButtonText = () => {
    switch (activeTab) {
      case "expenses":
        return "Add Expense";
      case "revenues":
        return "Add Revenue";
      case "events":
        return "Add Event";
      case "customers":
        return "Add Customer Projection";
      case "subscriptions":
        return "Add revenue plan";
      case "capacity":
        return "Add capacity group";
      default:
        return "Add New";
    }
  };

  return (
    <div className={`sh-panel overflow-hidden ${className}`}>
      {/* Header with tabs */}
      <div className="border-b border-white/10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {readOnly ? "Financial Data" : "Financial Data Management"}
            </h2>
            {!readOnly ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddNew}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                {getAddButtonText()}
              </Button>
            ) : null}
          </div>

          {/* Summary statistics */}
          <div className="flex items-center gap-4 mb-4 text-sm text-white/65">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-brand/15 border border-brand/30 rounded"></span>
              Revenues: {revenues.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-danger/15 border border-danger/30 rounded"></span>
              Expenses: {expenses.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-brand/15 border border-focus/30 rounded"></span>
              Events: {events.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-purple-500/10 border border-purple-500/30 rounded"></span>
              Customers: {customerProjections.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-orange-500/10 border border-orange-500/30 rounded"></span>
              Subscriptions: {subscriptionTiers.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-cyan-500/10 border border-cyan-500/30 rounded"></span>
              Capacity: {capacityGroups.length}
            </span>
          </div>

          {/* Tab navigation */}
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-brand/15 text-emerald-200 border border-brand/25"
                    : "text-white/65 hover:text-white hover:bg-white/[0.06]"
                }`}
              >
                {tab.label}
                <span className="ml-2 px-2 py-0.5 text-xs bg-white/10 text-white/75 rounded-full">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="p-6">
        {activeTab === "overview" ? (
          <FinancialDataGrid
            startupId={startupId}
            expenses={expenses}
            revenues={revenues}
            events={events}
            onExpenseClick={onExpenseClick}
            onRevenueClick={onRevenueClick}
            onEventClick={onEventClick}
            onRevisionReviseAgain={onRevisionReviseAgain}
          />
        ) : activeTab === "customers" ? (
          <CustomerProjectionGrid
            customerProjections={customerProjections}
            subscriptionTiers={subscriptionTiers}
            capacityGroups={capacityGroups}
            onProjectionClick={onCustomerProjectionClick}
          />
        ) : activeTab === "subscriptions" ? (
          <SubscriptionTierGrid
            subscriptionTiers={subscriptionTiers}
            onTierClick={onSubscriptionTierClick}
          />
        ) : activeTab === "capacity" ? (
          <CapacityGroupGrid
            capacityGroups={capacityGroups}
            customerProjections={customerProjections}
            onGroupClick={onCapacityGroupClick}
          />
        ) : activeTab === "team" ? (
          <TeamMemberGrid teamMembers={teamMembers} />
        ) : (
          <FinancialDataGrid
            startupId={startupId}
            expenses={tabData.expenses}
            revenues={tabData.revenues}
            events={tabData.events}
            onExpenseClick={onExpenseClick}
            onRevenueClick={onRevenueClick}
            onEventClick={onEventClick}
            onRevisionReviseAgain={onRevisionReviseAgain}
          />
        )}
      </div>

      {/* Forms Modal */}
      {!readOnly && showForm && (
        <Modal
          isOpen={true}
          onClose={() => setShowForm(null)}
          title={`Add ${showForm.charAt(0).toUpperCase() + showForm.slice(1)}`}
        >
          {showForm === "customer" && (
            <CustomerProjectionForm
              subscriptionTiers={subscriptionTiers}
              onSubmit={() => setShowForm(null)}
              onCancel={() => setShowForm(null)}
            />
          )}
          {showForm === "subscription" && (
            <SubscriptionTierForm
              onSubmit={() => setShowForm(null)}
              onCancel={() => setShowForm(null)}
            />
          )}
          {(showForm === "expense" ||
            showForm === "revenue" ||
            showForm === "event") && (
            <div className="p-6 text-center">
              <p className="text-white/65 mb-4">
                Form for {showForm} coming soon. Use the existing forms in the
                application for now.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowForm(null)}
              >
                Close
              </Button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

// Customer Projection Grid Component
interface CustomerProjectionGridProps {
  customerProjections: CustomerProjection[];
  subscriptionTiers: SubscriptionTier[];
  capacityGroups?: CustomerCapacityGroup[];
  onProjectionClick?: (projectionId: string) => void;
}

const CustomerProjectionGrid: React.FC<CustomerProjectionGridProps> = ({
  customerProjections,
  subscriptionTiers,
  capacityGroups = [],
  onProjectionClick,
}) => {
  return (
    <div className="space-y-4">
      <div className="text-sm text-white/65 mb-4">
        {customerProjections.length} customer projections
      </div>

      {customerProjections.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-white/45">No customer projections yet.</p>
          <p className="text-sm text-white/40 mt-2">
            Add your first customer projection to start modeling revenue growth.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {customerProjections.map((projection) => {
            const tier = subscriptionTiers.find(
              (t) => t.id === projection.subscriptionTierId,
            );
            const capacityGroup = capacityGroups.find(
              (group) => group.id === projection.capacityGroupId,
            );
            const summary = getCustomerProjectionCardSummary(
              projection,
              tier,
              capacityGroup,
            );
            return (
              <div
                key={projection.id}
                className="border border-white/10 rounded-lg p-4 hover:shadow-glow transition-shadow cursor-pointer"
                onClick={() => onProjectionClick?.(projection.id)}
              >
                <div className="mb-2 text-[10px] font-black uppercase tracking-wider text-white/40">
                  {summary.typeLabel}
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-white">{projection.name}</h3>
                    <p className="mt-1 text-sm font-semibold text-brand">
                      {summary.growthLine}
                    </p>
                    <p className="mt-1 text-sm text-white/55">
                      {summary.revenuePlanLine}
                    </p>
                    <ul className="mt-3 space-y-1">
                      {summary.detailLines.map((line) => (
                        <li key={line} className="text-xs text-white/45">
                          {line}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-bold text-brand">
                      {summary.revenueHighlight}
                    </div>
                    <div className="text-xs text-white/45">
                      {summary.revenueHighlightLabel}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface CapacityGroupGridProps {
  capacityGroups: CustomerCapacityGroup[];
  customerProjections: CustomerProjection[];
  onGroupClick?: (groupId: string) => void;
}

const CapacityGroupGrid: React.FC<CapacityGroupGridProps> = ({
  capacityGroups,
  customerProjections,
  onGroupClick,
}) => {
  return (
    <div className="space-y-4">
      <div className="text-sm text-white/65 mb-4">
        {capacityGroups.length} capacity group{capacityGroups.length === 1 ? "" : "s"}
      </div>

      {capacityGroups.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-white/45">No capacity groups yet.</p>
          <p className="text-sm text-white/40 mt-2">
            Add a shared location or operational ceiling, then link customer
            projections to it on their Capacity tab.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {capacityGroups.map((group) => {
            const linkedCount = customerProjections.filter(
              (projection) => projection.capacityGroupId === group.id,
            ).length;

            return (
              <div
                key={group.id}
                className="border border-white/10 rounded-lg p-4 hover:shadow-glow transition-shadow cursor-pointer"
                onClick={() => onGroupClick?.(group.id)}
              >
                <div className="text-[10px] font-black uppercase tracking-wider text-white/40">
                  Shared capacity
                </div>
                <h3 className="mt-1 font-medium text-white">{group.name}</h3>
                <div className="mt-2 text-2xl font-bold text-brand">
                  {group.maxCustomers.toLocaleString()}
                </div>
                <div className="text-xs text-white/45">max customers</div>
                {group.description ? (
                  <p className="mt-3 text-sm text-white/65">{group.description}</p>
                ) : null}
                <p className="mt-3 text-xs text-white/45">
                  {linkedCount} linked projection{linkedCount === 1 ? "" : "s"}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Revenue plan grid component
interface SubscriptionTierGridProps {
  subscriptionTiers: SubscriptionTier[];
  onTierClick?: (tierId: string) => void;
}

const SubscriptionTierGrid: React.FC<SubscriptionTierGridProps> = ({
  subscriptionTiers,
  onTierClick,
}) => {
  return (
    <div className="space-y-4">
      <div className="text-sm text-white/65 mb-4">
        {subscriptionTiers.length} revenue plans
      </div>

      {subscriptionTiers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-white/45">No revenue plans yet.</p>
          <p className="text-sm text-white/40 mt-2">
            Add your first revenue plan to define pricing for customer
            projections.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {subscriptionTiers.map((tier) => {
            const summary = getRevenuePlanCardSummary(tier);
            return (
            <div
              key={tier.id}
              className="border border-white/10 rounded-lg p-4 hover:shadow-glow transition-shadow cursor-pointer"
              onClick={() => onTierClick?.(tier.id)}
            >
              <div className="text-center">
                <div className="mb-2 text-[10px] font-black uppercase tracking-wider text-white/40">
                  {summary.modelLabel}
                </div>
                <h3 className="font-medium text-white mb-2">{tier.name}</h3>
                <div className="text-2xl font-bold text-brand mb-1">
                  {summary.priceLine}
                </div>
                {summary.detailLine ? (
                  <p className="text-sm text-white/55 mb-2">{summary.detailLine}</p>
                ) : null}
                {tier.description && (
                  <p className="text-sm text-white/65 mb-3">
                    {tier.description}
                  </p>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TeamMemberGrid: React.FC<{ teamMembers: TeamMember[] }> = ({ teamMembers }) => {
  if (teamMembers.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-white/45">
        No team members listed.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-white/45">
          <tr>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Title</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Compensation</th>
            <th className="px-3 py-2">Equity</th>
          </tr>
        </thead>
        <tbody>
          {teamMembers.map((member) => (
            <tr key={member.id} className="border-t border-white/10 text-white/80">
              <td className="px-3 py-3 font-medium text-white">{member.name}</td>
              <td className="px-3 py-3">{member.title}</td>
              <td className="px-3 py-3">{member.employmentType.replace("_", " ")}</td>
              <td className="px-3 py-3">
                {member.monthlyCashCost
                  ? `$${member.monthlyCashCost.toLocaleString()}/mo`
                  : member.compensationType.replace("_", " ")}
              </td>
              <td className="px-3 py-3">
                {member.equityPercent != null ? `${member.equityPercent}%` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EditViewGrid;
